import { Link } from "react-router-dom";
import { Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import React, { Fragment, useEffect, useRef, useState } from "react";
import PinField from "react-pin-field";

import { getDaffiLoginToken, reinstallFromDaffiAccount, sendEmailVerificationOTP, verifyEmailByOTP } from "actions/auth";
import { fetchingDataFromApi } from "actions/loaders";
import { getUserByToken } from "actions/profile";

import GeneratePin from "views/components/auth/GeneratePin";

const EmailOTPVerification = ({ userID, formData, registrationType, navigatePage }) => {
	const dispatch = useDispatch();

	const pinRef = useRef(null);

	const fetchingDataFromApi = useSelector((state) => state.FetchingDataFromApi);
	const deviceInfo = useSelector((state) => state.DeviceInfo);

	const [isCorrectPin, setIsCorrectPin] = useState(null);
	const [isUserVerified, setIsUserVerified] = useState(false);
	const [isEmailOTPSent, setIsEmailOTPSent] = useState(false);
	const [generatePin, setGeneratePin] = useState(false);
	const [userProfile, setUserProfile] = useState(null);

	useEffect(() => {
		setUserProfile({ ...formData });

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (userProfile?.password && generatePin === true) {
			dispatch(reinstallFromDaffiAccount(userProfile, navigatePage));
		}

		if (userProfile?.["access-token"]) {
			setGeneratePin(true);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userProfile]);

	const SendEmailOTP = async () => {
		dispatch(fetchingDataFromApi(true));

		await sendEmailVerificationOTP({ userID: userID }).then((data) => {
			if (data?.accepted) setIsEmailOTPSent(true);
		});

		dispatch(fetchingDataFromApi(false));
	};

	const VerifyOTP = (pin) => {
		verifyEmailByOTP({ userID: userID, otp: pin }, registrationType).then((data) => {
			if (data) setIsUserVerified(data);
		});
	};

	const DaffiUserLogin = () => {
		getDaffiLoginToken({
			username: userProfile?.username,
			password: userProfile?.password,
			"device-info": deviceInfo,
		}).then(async (accessToken) => {
			if (accessToken)
				await getUserByToken(accessToken).then((user) =>
					setUserProfile({ ...user, "access-token": accessToken, loginType: registrationType })
				);
		});
	};

	return (
		userProfile && (
			<Transition
				as={Fragment}
				appear={true}
				show={true}
				enter="transition-opacity duration-500"
				enterFrom="opacity-0"
				enterTo="opacity-100"
				leave="transition-opacity duration-150"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
			>
				<div className="w-full text-center text-sm text-gray-600 space-y-14">
					{isUserVerified ? (
						generatePin ? (
							<GeneratePin formData={userProfile} setFormData={setUserProfile} />
						) : (
							<div className="flex flex-col w-full items-center justify-center space-y-8">
								<div className="flex flex-col w-full items-center justify-center space-y-3">
									<p className={`text-lg font-bold text-dark-blue`}>
										Your new Daffi account is verified successfully
									</p>
								</div>

								<div className="flex flex-col w-full items-center justify-center space-y-3">
									<button
										onClick={() => DaffiUserLogin()}
										className="smooth py-3 px-8 rounded-lg bg-dark-blue text-white"
									>
										Login {userProfile?.username}
									</button>
									<Link to="/" className={`text-sm text-dark-blue hover:underline`}>
										Go back to dashboard
									</Link>
								</div>
							</div>
						)
					) : (
						<div className="flex flex-col w-full items-center justify-center space-y-5">
							<div className="flex flex-col items-center justify-center space-y-7">
								<p className="text-lg font-bold text-dark-blue">Successfully created Daffi account</p>

								{isEmailOTPSent ? (
									<div className="flex flex-col w-full items-center justify-center space-y-7">
										<div className="flex flex-col w-full items-center justify-center">
											<p className={`font-medium text-dark-blue`}>An OTP is sent to your email,</p>
											<p className={`font-medium text-dark-blue`}>
												If email not received check in spam emails
											</p>
										</div>
										<div className="flex flex-col w-full items-center justify-center space-y-3">
											<p className={`font-medium text-dark-blue`}>Enter OTP here</p>
											<div
												className={`flex items-center justify-center ${
													isCorrectPin === "invalid" && "animate-[bounce_75ms_linear_2]"
												}`}
											>
												<PinField
													className={`m-1 text-center border border-dark-blue outline-none smooth text-5xl pb-2 rounded-full text-dark-blue h-14 w-14 focus:border-4 invalid:animate-[bounce_75ms_linear_2] invalid:border-candy-red`}
													onComplete={(pin) => VerifyOTP(pin)}
													onChange={() => setIsCorrectPin(null)}
													ref={pinRef}
													length={6}
													validate="0123456789"
													type="password"
													autoFocus
												/>
											</div>
											<div className="flex w-full items-center justify-center space-x-1">
												<p className="text-sm text-dark-blue">Didn't receive OTP yet?</p>
												<button
													onClick={() => {
														setIsEmailOTPSent(false);
														SendEmailOTP();
													}}
													className="text-sm"
												>
													Send again
												</button>
											</div>
											{isCorrectPin === "invalid" && (
												<p className={`text-center text-sm text-candy-red`}>
													The Pin you have entered is invalid!
												</p>
											)}
										</div>
									</div>
								) : fetchingDataFromApi ? (
									<div className="flex flex-col w-full items-center justify-center space-y-5">
										<p className={`font-medium text-dark-blue`}>Sending OTP to: {formData?.email}</p>
										<img className="animate-spin mx-auto h-5 w-auto" src="/media/logo.png" alt="img" />
									</div>
								) : (
									<div className="flex flex-col w-full items-center justify-center space-y-7">
										<div className="flex flex-col items-center justify-center space-y-3">
											<p className={`font-medium text-dark-blue`}>
												Your account is not verified yet! please verify your account.
											</p>
										</div>
										<button
											onClick={() => SendEmailOTP()}
											className="smooth py-3 px-8 rounded-xl bg-dark-blue text-white hover:shadow-lg"
										>
											Verify Account
										</button>
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			</Transition>
		)
	);
};

export default EmailOTPVerification;
